/**
 * Zod schemas shared between backend and frontend. Backend uses these for
 * request validation in the Fastify route handlers; frontend uses them in
 * React Hook Form via the Zod resolver. Keeping the schemas in one place
 * means the validation rules cannot drift between the two sides.
 */

import { z } from 'zod';
import {
  CURRENT_STEP,
  INDUSTRY,
  LEAD_ACTION,
  LEAD_NEXT_ACTION,
  LEAD_SOURCE,
  LEAD_STAGE,
  OPPORTUNITY_ACTION,
  OPPORTUNITY_STAGE,
  TASK_PRIORITY,
  TASK_SOURCE,
  TASK_STATUS,
  TASK_TYPE,
  WIN_PROBABILITY,
} from './constants.js';

/** Reusable enum factory that mirrors a constant tuple into Zod. */
const enumFromTuple = <T extends readonly [string, ...string[]]>(values: T) => z.enum(values);

export const taskStatusSchema = enumFromTuple(TASK_STATUS);
export const taskTypeSchema = enumFromTuple(TASK_TYPE);
export const taskPrioritySchema = enumFromTuple(TASK_PRIORITY);
export const taskSourceSchema = enumFromTuple(TASK_SOURCE);
export const leadStageSchema = enumFromTuple(LEAD_STAGE);
export const leadSourceSchema = enumFromTuple(LEAD_SOURCE);
export const leadNextActionSchema = enumFromTuple(LEAD_NEXT_ACTION);
export const opportunityStageSchema = enumFromTuple(OPPORTUNITY_STAGE);
export const currentStepSchema = enumFromTuple(CURRENT_STEP);
export const winProbabilitySchema = enumFromTuple(WIN_PROBABILITY);
export const industrySchema = enumFromTuple(INDUSTRY);
export const leadActionSchema = enumFromTuple(LEAD_ACTION);
export const opportunityActionSchema = enumFromTuple(OPPORTUNITY_ACTION);

/** A trimmed non-empty string used for titles and names. */
export const nonEmptyString = z.string().trim().min(1).max(500);

/** ISO date string (YYYY-MM-DD) used for due dates and frog dates. */
export const isoDateString = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'must be an ISO date in the form YYYY-MM-DD');

/** Decimal value serialised as a string with up to 2 decimal places. */
export const decimalString = z
  .string()
  .regex(/^-?\d+(\.\d{1,2})?$/, 'must be a decimal with up to 2 decimal places');

/** Task creation payload. Smart defaults applied server-side. */
export const createTaskSchema = z.object({
  title: nonEmptyString,
  body: z.string().max(50_000).optional(),
  status: taskStatusSchema.optional(),
  type: taskTypeSchema.optional(),
  priority: taskPrioritySchema.optional(),
  dueDate: isoDateString.nullable().optional(),
  sourceField: taskSourceSchema.optional(),
});
export type CreateTaskInput = z.infer<typeof createTaskSchema>;

/** Task partial-update payload. Every field is optional; the server
 * writes one audit log entry per changed field. */
export const updateTaskSchema = z
  .object({
    title: nonEmptyString.optional(),
    body: z.string().max(50_000).nullable().optional(),
    status: taskStatusSchema.optional(),
    type: taskTypeSchema.optional(),
    priority: taskPrioritySchema.optional(),
    dueDate: isoDateString.nullable().optional(),
    snoozeUntil: isoDateString.nullable().optional(),
  })
  .strict();
export type UpdateTaskInput = z.infer<typeof updateTaskSchema>;

/** Body for the drag-and-drop move endpoint. */
export const moveTaskSchema = z.object({
  status: taskStatusSchema,
  beforeTaskId: z.string().uuid().optional(),
  afterTaskId: z.string().uuid().optional(),
});
export type MoveTaskInput = z.infer<typeof moveTaskSchema>;

/** Body for snooze. */
export const snoozeTaskSchema = z.object({
  until: isoDateString,
});
export type SnoozeTaskInput = z.infer<typeof snoozeTaskSchema>;

/** Body for frog (optional explicit date). */
export const frogTaskSchema = z
  .object({
    date: isoDateString.optional(),
  })
  .optional();
export type FrogTaskInput = z.infer<typeof frogTaskSchema>;

/**
 * Query parameters for listing tasks.
 *
 * `includeCompletedSince` (UAT-45) extends the default open-task listing
 * with rows completed on or after the supplied calendar date. It is the
 * mechanism the kanban Board uses to populate the DONE column with the
 * current day's completions without surfacing yesterday's; the Board
 * passes today's ISO date and the server widens the `completedAt`
 * filter to an OR of `null` plus `gte: <since>`. Other surfaces (Today,
 * Table) omit the param and continue to receive only open tasks via the
 * default `completedAt: null` filter.
 */
export const listTasksQuerySchema = z
  .object({
    status: taskStatusSchema.optional(),
    type: taskTypeSchema.optional(),
    priority: taskPrioritySchema.optional(),
    completed: z
      .union([z.boolean(), z.enum(['true', 'false'])])
      .transform((v) => (typeof v === 'string' ? v === 'true' : v))
      .default(false),
    /**
     * ISO date (YYYY-MM-DD). When set, the list response also includes
     * tasks whose `completedAt` is on or after the supplied date. The
     * `completed` flag still wins when explicitly `true` (the
     * completed-only path remains unchanged).
     */
    includeCompletedSince: isoDateString.optional(),
    search: z.string().trim().max(200).optional(),
    /**
     * Lower bound on `completedAt` when `completed=true`.
     *
     * Accepts the wire-format `YYYY-MM-DD` calendar date the frontend
     * already uses for `dueDate` and `todayIso()`. The route inflates
     * the supplied date to a UTC midnight Date and passes it through as
     * `completedAt: { gte }` so the Today + Table views can request the
     * last seven days of completions without dragging the full archive
     * across the wire. Ignored when `completed=false` (the open list
     * already excludes every completed row).
     *
     * Added in the Round 5 Tasks-views rewrite (UAT-44) so the new
     * Completed today / Completed this week sections page realistic
     * windows.
     */
    since: isoDateString.optional(),
    limit: z.coerce.number().int().positive().max(500).default(200),
    offset: z.coerce.number().int().nonnegative().default(0),
  })
  .strict();
export type ListTasksQuery = z.infer<typeof listTasksQuerySchema>;

/** Body for creating a recurring task template. */
export const createRecurringTemplateSchema = z.object({
  titleTemplate: nonEmptyString,
  bodyTemplate: z.string().max(50_000).optional(),
  type: taskTypeSchema,
  priority: taskPrioritySchema,
  recurrenceRule: z.string().trim().min(1),
  nextSpawnAt: z.string().datetime({ message: 'must be an ISO datetime' }).optional(),
});
export type CreateRecurringTemplateInput = z.infer<typeof createRecurringTemplateSchema>;

/** Partial update for a recurring template. */
export const updateRecurringTemplateSchema = z
  .object({
    titleTemplate: nonEmptyString.optional(),
    bodyTemplate: z.string().max(50_000).nullable().optional(),
    type: taskTypeSchema.optional(),
    priority: taskPrioritySchema.optional(),
    recurrenceRule: z.string().trim().min(1).optional(),
    nextSpawnAt: z.string().datetime({ message: 'must be an ISO datetime' }).optional(),
    active: z.boolean().optional(),
  })
  .strict();
export type UpdateRecurringTemplateInput = z.infer<typeof updateRecurringTemplateSchema>;

// ────────────────────────────────────────────────────────────────────
// Sales (M6)
// ────────────────────────────────────────────────────────────────────

/** Lead creation payload per spec section 6.4. */
export const createLeadSchema = z.object({
  companyName: nonEmptyString,
  contactName: z.string().trim().max(200).optional(),
  contactRole: z.string().trim().max(200).optional(),
  contactEmail: z.string().email().optional(),
  contactPhone: z.string().trim().max(50).optional(),
  source: leadSourceSchema,
  oppType: z.string().trim().max(200).optional(),
  capability: z.enum(['AI', 'XR', 'SIMULATION', 'DIGITAL_TWIN']).optional(),
  customerType: z.enum(['ENTERPRISE', 'GOVERNMENT', 'SMB', 'UNIVERSITY']).optional(),
  /** Step 1: enum industry. `industryVertical` stays accepted during the
   * transitional release for any caller still sending the free string. */
  industry: industrySchema.optional(),
  industryVertical: z.string().trim().max(200).optional(),
  currentCustomer: z.boolean().optional(),
  potentialValue: decimalString.optional(),
  notes: z.string().max(50_000).optional(),
});
export type CreateLeadInput = z.infer<typeof createLeadSchema>;

/** Partial update for a lead. */
export const updateLeadSchema = z
  .object({
    companyName: nonEmptyString.optional(),
    contactName: z.string().trim().max(200).nullable().optional(),
    contactRole: z.string().trim().max(200).nullable().optional(),
    contactEmail: z.string().email().nullable().optional(),
    contactPhone: z.string().trim().max(50).nullable().optional(),
    source: leadSourceSchema.optional(),
    stage: leadStageSchema.optional(),
    oppType: z.string().trim().max(200).nullable().optional(),
    capability: z.enum(['AI', 'XR', 'SIMULATION', 'DIGITAL_TWIN']).nullable().optional(),
    customerType: z.enum(['ENTERPRISE', 'GOVERNMENT', 'SMB', 'UNIVERSITY']).nullable().optional(),
    industry: industrySchema.nullable().optional(),
    industryVertical: z.string().trim().max(200).nullable().optional(),
    currentCustomer: z.boolean().optional(),
    potentialValue: decimalString.optional(),
    notes: z.string().max(50_000).nullable().optional(),
    // Step 1: the canonical user-set action. The server derives the next
    // action and the stage from it. CONVERTED_TO_OPPORTUNITY is rejected
    // here (Convert button only); DISQUALIFIED routes the lead to DEAD.
    currentAction: leadActionSchema.optional(),
    // Tabs-updates: the queue snooze defers the staleness clock via this
    // timestamp (ISO datetime). `nextActionDue` is retained transitionally
    // but no longer drives the queue (Due removed).
    snoozeUntil: z.string().datetime().nullable().optional(),
    nextActionDue: isoDateString.nullable().optional(),
    // PR #34 (UAT-34) added inline editing of the planned next action.
    // The `nextAction` column is also written by the log-action endpoint,
    // but Peter wants to nudge it from the slide-over without recording
    // a log entry, so the PATCH path accepts it directly.
    nextAction: leadNextActionSchema.optional(),
  })
  .strict();
export type UpdateLeadInput = z.infer<typeof updateLeadSchema>;

/** Body for POST /leads/:id/log-action. */
export const logLeadActionSchema = z.object({
  action: z.enum([
    'PLAN_OUTREACH',
    'SEND_OUTREACH',
    'FOLLOW_UP',
    'BOOK_MEETING',
    'HOLD_MEETING',
    'SEND_POST_MEETING_FOLLOW_UP',
    'QUALIFY',
  ]),
  occurredAt: z.string().datetime().optional(),
  summary: z.string().max(2_000).optional(),
  meetingDate: isoDateString.optional(),
});
export type LogLeadActionInput = z.infer<typeof logLeadActionSchema>;

/** Body for POST /leads/:id/convert. */
export const convertLeadSchema = z.object({
  opportunityName: nonEmptyString,
  oppType: z.enum(['PROPOSAL', 'TENDER', 'GRANT', 'VARIATION', 'OTHER']).optional(),
  capability: z.enum(['AI', 'XR', 'SIMULATION', 'DIGITAL_TWIN']).optional(),
  budgetFee: decimalString.optional(),
  budgetSignal: z.enum(['UNKNOWN', 'WEAK', 'MEDIUM', 'STRONG', 'CONFIRMED']).optional(),
});
export type ConvertLeadInput = z.infer<typeof convertLeadSchema>;

/** Opportunity creation payload per spec section 6.5. */
export const createOpportunitySchema = z.object({
  companyName: nonEmptyString,
  opportunityName: nonEmptyString,
  // Step 1: contact carried over from the originating lead.
  contactName: z.string().trim().max(200).optional(),
  contactEmail: z.string().email().optional(),
  contactPhone: z.string().trim().max(50).optional(),
  oppType: z.enum(['PROPOSAL', 'TENDER', 'GRANT', 'VARIATION', 'OTHER']),
  capability: z.enum(['AI', 'XR', 'SIMULATION', 'DIGITAL_TWIN']).optional(),
  source: leadSourceSchema,
  budgetFee: decimalString.optional(),
  budgetSignal: z.enum(['UNKNOWN', 'WEAK', 'MEDIUM', 'STRONG', 'CONFIRMED']).optional(),
  customerType: z.enum(['ENTERPRISE', 'GOVERNMENT', 'SMB', 'UNIVERSITY']).optional(),
  customerStatus: z.enum(['NEW_CUSTOMER', 'EXISTING_CUSTOMER', 'NEW_LEAD']).optional(),
  proposalDocUrl: z.string().url().optional(),
  notes: z.string().max(50_000).optional(),
});
export type CreateOpportunityInput = z.infer<typeof createOpportunitySchema>;

/** Partial update for an opportunity. */
export const updateOpportunitySchema = z
  .object({
    companyName: nonEmptyString.optional(),
    opportunityName: nonEmptyString.optional(),
    oppType: z.enum(['PROPOSAL', 'TENDER', 'GRANT', 'VARIATION', 'OTHER']).optional(),
    capability: z.enum(['AI', 'XR', 'SIMULATION', 'DIGITAL_TWIN']).nullable().optional(),
    contactName: z.string().trim().max(200).nullable().optional(),
    contactEmail: z.string().email().nullable().optional(),
    contactPhone: z.string().trim().max(50).nullable().optional(),
    stage: opportunityStageSchema.optional(),
    // Step 1: canonical user-set action; server derives nextAction + stage.
    // WON / LOST are rejected here (Mark Won / Mark Lost buttons only).
    currentAction: opportunityActionSchema.optional(),
    currentStep: currentStepSchema.optional(),
    winProbability: winProbabilitySchema.optional(),
    budgetFee: decimalString.nullable().optional(),
    budgetSignal: z.enum(['UNKNOWN', 'WEAK', 'MEDIUM', 'STRONG', 'CONFIRMED']).optional(),
    customerType: z.enum(['ENTERPRISE', 'GOVERNMENT', 'SMB', 'UNIVERSITY']).nullable().optional(),
    customerStatus: z.enum(['NEW_CUSTOMER', 'EXISTING_CUSTOMER', 'NEW_LEAD']).optional(),
    source: leadSourceSchema.optional(),
    proposalDocUrl: z.string().url().nullable().optional(),
    notes: z.string().max(50_000).nullable().optional(),
    // Tabs-updates: queue snooze timestamp (see updateLeadSchema).
    snoozeUntil: z.string().datetime().nullable().optional(),
    nextActionDue: isoDateString.nullable().optional(),
  })
  .strict();
export type UpdateOpportunityInput = z.infer<typeof updateOpportunitySchema>;

/** Body for POST /opportunities/:id/advance-step. */
export const advanceOpportunityStepSchema = z.object({
  currentStep: currentStepSchema,
  /** Optional override of the suggested stage. */
  stage: opportunityStageSchema.optional(),
  /** Optional override of the suggested probability. */
  winProbability: winProbabilitySchema.optional(),
});
export type AdvanceOpportunityStepInput = z.infer<typeof advanceOpportunityStepSchema>;

/** Health endpoint response envelope. */
export const healthResponseSchema = z.object({
  status: z.literal('ok'),
  db: z.enum(['ok', 'error']),
  version: z.string(),
});
export type HealthResponse = z.infer<typeof healthResponseSchema>;

/**
 * Canonical settings schema per spec section 4.7. All keys optional on
 * the patch payload; the backend merges with defaults so the GET
 * response always returns every key.
 */
export const settingsSchema = z
  .object({
    'tasks.waitingFollowUpDays': z.number().int().min(0).max(60),
    'tasks.wipLimitDoing': z.number().int().min(1).max(10),
    'tasks.autoPullEnabled': z.boolean(),
    'tasks.defaultPriority': taskPrioritySchema,
    'sales.dailyCommitTargetActions': z.number().int().min(0).max(20),
    'sales.unclearedQueueBannerHour': z.number().int().min(0).max(23),
    'sales.stalledTargetDays': z.number().int().min(1).max(180),
    'sales.stalledAwaitingDays': z.number().int().min(1).max(180),
    'sales.stalledActiveDays': z.number().int().min(1).max(180),
    'sales.driftingStepDays': z.number().int().min(1).max(180),
    'sales.freshRecordDays': z.number().int().min(1).max(180),
    'sales.subtaskTemplatesAutoApply': z.boolean(),
    'sales.zeroDayFlagEnabled': z.boolean(),
    'subtaskTemplates.newLead': z.array(z.string().min(1).max(200)).max(20),
    'subtaskTemplates.newOpp': z.array(z.string().min(1).max(200)).max(20),
    'subtaskTemplates.atProposal': z.array(z.string().min(1).max(200)).max(20),
    'subtaskTemplates.postMeeting': z.array(z.string().min(1).max(200)).max(20),
    'claude.apiKey': z.string().min(0).max(500).nullable(),
    'claude.briefTemplate': z.string().max(5_000),
    'ui.sidebarCollapsed': z.boolean(),
  })
  .strict();
export type SettingsState = z.infer<typeof settingsSchema>;
export const settingsPatchSchema = settingsSchema.partial();
export type SettingsPatch = z.infer<typeof settingsPatchSchema>;

/** Subtask CRUD payloads for the lead and opportunity slide-overs. */
export const subtaskCreateSchema = z.object({
  title: z.string().min(1).max(500),
});
export type SubtaskCreateInput = z.infer<typeof subtaskCreateSchema>;

export const subtaskUpdateSchema = z
  .object({
    title: z.string().min(1).max(500).optional(),
    done: z.boolean().optional(),
    orderIndex: z.number().int().min(0).max(1_000).optional(),
  })
  .strict();
export type SubtaskUpdateInput = z.infer<typeof subtaskUpdateSchema>;
